import React, { useEffect, useMemo, useState } from "react";
import { useNavigate }  from "react-router-dom";
import { jsPDF }        from "jspdf";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent,
  Checkbox, Chip, CircularProgress, Container, Divider,
  FormControl, Grid, InputAdornment, InputLabel, MenuItem,
  Pagination, Paper, Select, Skeleton, Stack, Step,
  StepLabel, Stepper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";

//import ArrowBackIcon           from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import FilterListIcon          from "@mui/icons-material/FilterList";
import PaymentIcon             from "@mui/icons-material/Payment";
import PersonIcon              from "@mui/icons-material/Person";
import PrintIcon               from "@mui/icons-material/Print";
import ReceiptIcon             from "@mui/icons-material/Receipt";
import SearchIcon              from "@mui/icons-material/Search";
import Footer                  from "../Footer/Footer";
import { getUsersByOrgId } from "../Services/userService";
import { generateReceiptByScreen, getOutstanding }  from "../Services/InvoiceReceiptService";
import { IInvoice }        from "../Interfaces/IInvoice";
import { getOrganizationDetails } from "../Services/orgService";
import { IOrgDetails} from "../Interfaces/IorgProfile";
import { ICustomer } from "../Interfaces/IUser";

// ─── Types ────────────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

function numberToWords(amount: number): string {
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
    "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen",
    "Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];

  function convert(n: number): string {
    if (n < 20)       return ones[n];
    if (n < 100)      return tens[Math.floor(n/10)] + (n%10 ? " "+ones[n%10] : "");
    if (n < 1000)     return ones[Math.floor(n/100)] + " Hundred" + (n%100 ? " "+convert(n%100) : "");
    if (n < 100000)   return convert(Math.floor(n/1000)) + " Thousand" + (n%1000 ? " "+convert(n%1000) : "");
    if (n < 10000000) return convert(Math.floor(n/100000)) + " Lakh" + (n%100000 ? " "+convert(n%100000) : "");
    return convert(Math.floor(n/10000000)) + " Crore" + (n%10000000 ? " "+convert(n%10000000) : "");
  }

  const rupees = Math.floor(amount);
  const paise  = Math.round((amount - rupees) * 100);
  return "Rupees " + convert(rupees) + (paise > 0 ? " and "+convert(paise)+" Paise" : "");
}

// ─── PDF Generator ────────────────────────────────────────────────────────────

function printReceiptPDF(p: {
  orgDetails:       IOrgDetails;
  customer:         ICustomer;
  receiptId:        string;
  referenceIds:     string[];   // single or multi invoice IDs
  paidAmount:       number;
  paymentMode:      string;
  receiptDate:      string;
  utrNo:            string;
  totalOutstanding: number;
  balanceAfter:     number;
}) {
  const doc = new jsPDF();
  const W   = doc.internal.pageSize.width;
  const H   = doc.internal.pageSize.height;

  // Header
  doc.setFontSize(14); doc.setFont("helvetica","bold");
  doc.text("PAYMENT RECEIPT", W/2, 12, { align:"center" });

  // Org box (left) — mirrors your generateBaseInvoicePDF style
  doc.rect(14, 18, 88, 50);
  doc.setFontSize(12); doc.setFont("helvetica","bold");
  doc.text(p.orgDetails?.OrgName || "N/A", 16, 27);
  doc.setFontSize(9); doc.setFont("helvetica","normal");
  const orgAddr = `Address: ${p.orgDetails?.address?.[0]?.addressLine1||""}, ${p.orgDetails?.address?.[0]?.addressLine2||""}, ${p.orgDetails?.address?.[0]?.city||""}, ${p.orgDetails?.address?.[0]?.state||""} - ${p.orgDetails?.address?.[0]?.zipCode||""}`;
  const wOA = doc.splitTextToSize(orgAddr, 84);
  doc.text(wOA, 16, 33);
  let oy = 33 + wOA.length * 4;
  doc.text(`E-Mail: ${p.orgDetails?.email||""}`,         16, oy+2);
  doc.text(`Contact No.: ${p.orgDetails?.phone||""}`,    16, oy+7);
  doc.text(`GSTIN/UIN: ${p.orgDetails?.GST||""}`,        16, oy+12);
  doc.text(`State: ${p.orgDetails?.address?.[0]?.state||""}`, 16, oy+17);

  // Receipt details box (right)
  doc.rect(108, 18, 88, 50);
  doc.setFontSize(9); doc.setFont("helvetica","bold");
  doc.text("Receipt No.:", 110, 26); doc.setFont("helvetica","normal");
  doc.text(p.receiptId, 110, 32);
  doc.setFont("helvetica","bold"); doc.text("Date:", 110, 42); doc.setFont("helvetica","normal");
  doc.text(new Date(p.receiptDate).toLocaleDateString("en-GB"), 110, 48);
  doc.setFont("helvetica","bold"); doc.text("Payment Mode:", 110, 56); doc.setFont("helvetica","normal");
  doc.text(p.paymentMode, 110, 62);

  // Received From box
  let y = 78;
  doc.rect(14, y, 182, 42);
  doc.setFontSize(9); doc.setFont("helvetica","normal");
  doc.text("Received From:", 16, y+7);
  doc.setFontSize(11); doc.setFont("helvetica","bold");
  const custName  = p.customer?.customerName ?? p.customer?.companyName ?? "";
  const wCN       = doc.splitTextToSize(custName, 80);
  doc.text(wCN, 16, y+14);
  doc.setFontSize(9); doc.setFont("helvetica","normal");
  const custAddr  = [p.customer?.street, p.customer?.city, p.customer?.state, p.customer?.zipCode].filter(Boolean).join(", ");
  const wCA       = doc.splitTextToSize(custAddr, 80);
  const cnH       = wCN.length * 5;
  doc.text(wCA, 16, y+14+cnH);
  doc.text(`GSTIN/UIN: ${p.customer?.GSTIN||"N/A"}`, 16, y+14+cnH+wCA.length*4+2);
  doc.text(`Phone: ${p.customer?.phone||""}`, 110, y+14);
  doc.text(`State: ${p.customer?.state||""}`, 110, y+20);

  // Amount box
  y = 128;
  doc.rect(14, y, 182, 14);
  doc.setFontSize(11); doc.setFont("helvetica","bold");
  doc.text("Amount Received (Rs.):", 16, y+9);
  doc.setFontSize(13);
  doc.text(p.paidAmount.toFixed(2), 92, y+9);

  // Details table
  y = 150;
  const refLabel = p.referenceIds.length > 1
    ? `${p.referenceIds.length} Invoices`
    : (p.referenceIds[0] || "General Payment");

  const rows: [string,string][] = [
    ["Against Invoice / Reference", refLabel],
    ...(p.referenceIds.length > 1
      ? [["Invoice Nos.", p.referenceIds.join(", ")] as [string,string]]
      : []),
    ["Payment Mode",              p.paymentMode],
    ["Reference / UTR No.",       p.utrNo || "N/A"],
    ["Outstanding (before)",      `Rs. ${p.totalOutstanding.toFixed(2)}`],
    ["Amount Paid",               `Rs. ${p.paidAmount.toFixed(2)}`],
    ["Balance Outstanding (after)",`Rs. ${p.balanceAfter.toFixed(2)}`],
  ];

  doc.setFontSize(9);
  rows.forEach(([label,value], i) => {
    const ry = y + i*9;
    doc.rect(14, ry, 90, 9); doc.rect(104, ry, 92, 9);
    doc.setFont("helvetica","bold");   doc.text(label, 16, ry+6);
    doc.setFont("helvetica","normal"); doc.text(value, 106, ry+6);
  });

  // Amount in words
  y = y + rows.length*9 + 8;
  doc.setFont("helvetica","italic");
  const wW = doc.splitTextToSize(`Amount in Words: ${numberToWords(p.paidAmount)} Only`, 180);
  doc.text(wW, 14, y);

  // Bank details
  y += wW.length*5 + 8;
  doc.setFont("helvetica","bold");   doc.text("Payment Instructions:", 14, y);
  doc.setFont("helvetica","normal");
  doc.text(`A/c Holder Name: ${p.orgDetails?.accountDetails?.[0]?.accountHolderName||"N/A"}`, 14, y+5);
  doc.text(`Account No.: ${p.orgDetails?.accountDetails?.[0]?.accountNumber||"N/A"}`,          14, y+10);
  doc.text(`IFSC: ${p.orgDetails?.accountDetails?.[0]?.ifscCode||"N/A"}`,                      14, y+15);
  doc.text(`Bank Name: ${p.orgDetails?.accountDetails?.[0]?.bankName||"N/A"}`,                 14, y+20);

  // Terms
  doc.setFontSize(8); doc.setFont("helvetica","normal");
  const wT = doc.splitTextToSize(`Terms and Conditions: ${p.orgDetails?.termAndCondition||""}`, 120);
  doc.text(wT, 14, H-42);

  // Authorised signatory
  doc.setFontSize(12); doc.setFont("helvetica","bold"); doc.setTextColor(0);
  doc.text(`for ${p.orgDetails?.OrgName||""}`, W-76, H-42);
  doc.line(W-82, H-26, W-14, H-26);
  doc.setFontSize(9); doc.setFont("helvetica","normal");
  doc.text("Authorised Signatory", W-74, H-20);

  // Footer
  doc.setFontSize(9); doc.setTextColor(150);
  doc.text("This is a Computer Generated Receipt", W/2-35, H-3);

  doc.save(`Receipt_${p.receiptId}.pdf`);
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, color }: { label:string; value:string; color?:string }) => (
  <Card variant="outlined" sx={{ borderRadius:2 }}>
    <CardContent sx={{ py:1.5,"&:last-child":{ pb:1.5 } }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h6" fontWeight={600} color={color||"text.primary"}>{value}</Typography>
    </CardContent>
  </Card>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS     = ["Select Customer","Review Outstanding","Payment Details"];
const PAGE_SIZE = 5;

// ─── Component ────────────────────────────────────────────────────────────────

const GenerateReceipt: React.FC = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));

  // ── State ─────────────────────────────────────────────────────────────────

  const [orgId,      setOrgId]      = useState("");
  const [orgDetails, setOrgDetails] = useState<IOrgDetails|null>(null);
  const createdBy = localStorage.getItem("userId") ?? "";

  const [activeStep, setActiveStep] = useState(0);

  // Step 1
  const [customers,        setCustomers]        = useState<ICustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer|null>(null);

  // Step 2
  const [invoices,        setInvoices]        = useState<IInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedInvoices,setSelectedInvoices]= useState<IInvoice[]>([]);  // multi-select
  const [invoiceSearch,   setInvoiceSearch]   = useState("");
  const [statusFilter,    setStatusFilter]    = useState<"all"|"due"|"paid">("due");
  const [invoicePage,     setInvoicePage]     = useState(1);

  // Step 3
  const [paidAmount,  setPaidAmount]  = useState("");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split("T")[0]);
  const [utrNo,       setUtrNo]       = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successData, setSuccessData] = useState<{
    receiptId:string; paidAmount:number; balanceAfter:number;
  }|null>(null);

  // ── Load orgId + orgDetails from localStorage (immediate, no flicker) ─────
  useEffect(() => {
    const id = localStorage.getItem("orgId") || "";
    setOrgId(id);
    // Load org details immediately from localStorage as fallback
    // This ensures PDF always works even if the API call fails
    const stored = localStorage.getItem("orgDetails");
    if (stored) {
      try { setOrgDetails(JSON.parse(stored)); } catch { /* ignore */ }
    }
  }, []);

  // ── Fetch latest orgDetails from API ─────────────────────────────────────
  // useEffect(() => {
  //   if (!orgId) return;
  //   axios.get(`/api/org/${orgId}`)
  //     .then((r) => setOrgDetails(r.data))
  //     .catch(() => { /* localStorage fallback already set above */ });
  // }, [orgId]);
  useEffect(() => {
  const run = async () => {
    if (!orgId) return;

    try {
      const data = await getOrganizationDetails(orgId);
      setOrgDetails(data);
    } catch (err) {
      console.error("Failed to load org details:", err);

      // fallback (your existing logic)
      const s = localStorage.getItem("orgDetails");
      if (s) setOrgDetails(JSON.parse(s));
    }
  };

  run();
}, [orgId]);

  // ── Fetch customer list ───────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    const run = async () => {
      setCustomersLoading(true);
      try {
        const res = await getUsersByOrgId(orgId);
        setCustomers(
          (res||[]).filter((c:ICustomer) =>
            c.userType === "customer" && c.customerName?.trim()
          )
        );
      } catch { setCustomers([]); }
      finally { setCustomersLoading(false); }
    };
    run();
  }, [orgId]);

  // ── Fetch invoices when customer changes ──────────────────────────────────
  useEffect(() => {
    if (!selectedCustomer || !orgId) { setInvoices([]); return; }
    const run = async () => {
      setInvoicesLoading(true);
      try {
        const res = await getOutstanding(orgId, selectedCustomer.Id);
        setInvoices(res || []);
      } catch { setInvoices([]); }
      finally { setInvoicesLoading(false); }
    };
    run();
  }, [selectedCustomer, orgId]);

  // ── Filtered + paginated invoices ─────────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    const q = invoiceSearch.toLowerCase();
    return invoices.filter((inv) => {
      const matchSearch = !q ||
        (inv.invoiceId||"").toLowerCase().includes(q) ||
        (inv.invoiceNumber||"").toLowerCase().includes(q);
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "due"  && Number(inv.dueAmount) > 0) ||
        (statusFilter === "paid" && Number(inv.dueAmount) <= 0);
      return matchSearch && matchStatus;
    });
  }, [invoices, invoiceSearch, statusFilter]);

  const totalPages    = Math.ceil(filteredInvoices.length / PAGE_SIZE);
  const pagedInvoices = filteredInvoices.slice(
    (invoicePage - 1) * PAGE_SIZE,
    invoicePage * PAGE_SIZE
  );

  // ── Derived values ────────────────────────────────────────────────────────
  const totalOutstanding = invoices.reduce((s,i) => s + Number(i.dueAmount||0), 0);
  const totalBilled      = invoices.reduce((s,i) => s + Number(i.grandTotal||0), 0);
  const totalPaidSoFar   = invoices.reduce((s,i) => s + Number(i.totalPaidAmount||0), 0);
  const selectedDueTotal = selectedInvoices.reduce((s,i) => s + Number(i.dueAmount||0), 0);
  const amount           = parseFloat(paidAmount) || 0;
  const balanceAfter     = Math.max(totalOutstanding - amount, 0);
  const overpayment      = amount > totalOutstanding && totalOutstanding > 0;

  // ── Invoice selection helpers ─────────────────────────────────────────────
  const isSelected = (inv:IInvoice) =>
    selectedInvoices.some((i) => i.invoiceId === inv.invoiceId);

  const toggleInvoice = (inv:IInvoice) => {
    if (Number(inv.dueAmount) <= 0) return;
    setSelectedInvoices((prev) => {
      const next = isSelected(inv)
        ? prev.filter((i) => i.invoiceId !== inv.invoiceId)
        : [...prev, inv];
      // Auto-fill amount as sum of selected due amounts
      const sum = next.reduce((s,i) => s + Number(i.dueAmount||0), 0);
      setPaidAmount(sum > 0 ? sum.toFixed(2) : "");
      return next;
    });
    setSuccessData(null);
  };

  const pendingOnPage  = pagedInvoices.filter((i) => Number(i.dueAmount) > 0);
  const allPageSelected= pendingOnPage.length > 0 && pendingOnPage.every(isSelected);
  const someSelected   = pendingOnPage.some(isSelected) && !allPageSelected;

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      // deselect page items
      setSelectedInvoices((prev) =>
        prev.filter((si) => !pendingOnPage.some((pi) => pi.invoiceId === si.invoiceId))
      );
    } else {
      // add missing page items
      const toAdd = pendingOnPage.filter((i) => !isSelected(i));
      setSelectedInvoices((prev) => {
        const next = [...prev, ...toAdd];
        const sum  = next.reduce((s,i) => s + Number(i.dueAmount||0), 0);
        setPaidAmount(sum > 0 ? sum.toFixed(2) : "");
        return next;
      });
    }
    setSuccessData(null);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCustomerSelect = (c:ICustomer|null) => {
    setSelectedCustomer(c);
    setSelectedInvoices([]);
    setPaidAmount("");
    setSuccessData(null);
    setSubmitError("");
    setInvoiceSearch("");
    setStatusFilter("due");
    setInvoicePage(1);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || amount <= 0) return;
    setSubmitting(true); setSubmitError("");
    try {
      // Build referenceId: single or comma-separated multi
      const referenceId =
        selectedInvoices.length === 1   ? selectedInvoices[0].invoiceId :
        selectedInvoices.length >  1   ? selectedInvoices.map((i) => i.invoiceId).join(",") :
        "GENERAL";

      // ── Payload matches your generateReceipt API exactly ──
      const payload = {
        orgId,
        customerId:    selectedCustomer.Id,
        customerName:  selectedCustomer.customerName ?? selectedCustomer.companyName ?? "",
        referenceId,
        referenceType: (selectedInvoices.length > 0 ? "Invoice" : "General") as "Invoice" | "General",
        paidAmount:    amount,       
        paymentMode,
        createdBy:     createdBy || orgId,
      };

      const res = await generateReceiptByScreen(payload);
      const receiptId = res.receiptId;

      setSuccessData({ receiptId, paidAmount: amount, balanceAfter });
      setActiveStep(3);

      // Print PDF — orgDetails already loaded from localStorage if API was slow
      if (orgDetails) {
        printReceiptPDF({
          orgDetails,
          customer:         selectedCustomer,
          receiptId,
          referenceIds:     selectedInvoices.length > 0
                              ? selectedInvoices.map((i) => i.invoiceId)
                              : ["GENERAL"],
          paidAmount:       amount,
          paymentMode,
          receiptDate,
          utrNo,
          totalOutstanding,
          balanceAfter,
        });
      }
    } catch (err:any) {
      setSubmitError(
        err?.response?.data?.message || err?.message || "Failed to generate receipt."
      );
    } finally { setSubmitting(false); }
  };

  const handlePrintAgain = () => {
    if (!orgDetails || !selectedCustomer || !successData) return;
    printReceiptPDF({
      orgDetails,
      customer:         selectedCustomer,
      receiptId:        successData.receiptId,
      referenceIds:     selectedInvoices.length > 0
                          ? selectedInvoices.map((i) => i.invoiceId)
                          : ["GENERAL"],
      paidAmount:       successData.paidAmount,
      paymentMode,
      receiptDate,
      utrNo,
      totalOutstanding,
      balanceAfter:     successData.balanceAfter,
    });
  };

  const handleReset = () => {
    setSelectedCustomer(null); setSelectedInvoices([]); setInvoices([]);
    setPaidAmount(""); setPaymentMode("Cash");
    setReceiptDate(new Date().toISOString().split("T")[0]);
    setUtrNo(""); setSubmitError(""); setSuccessData(null);
    setActiveStep(0); setInvoiceSearch(""); setStatusFilter("due"); setInvoicePage(1);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <><div className="page-location">
      <span>Receipt &gt; Generate Receipts</span>
    </div>
     <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 1 }}>
    {/* <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}> */}
        {/* Breadcrumb */}
        {/* <Stack direction="row" alignItems="center" gap={1} mb={2}>
          {/* <Button startIcon={<ArrowBackIcon/>} size="small"
      onClick={() => navigate(-1)} sx={{ textTransform:"none" }}>
      Back
    </Button> */}
          {/* <Typography variant="caption" color="text.secondary">
      Receipt › Generate Receipt
    </Typography> */}
          {/* <div className="page-location">
    <span>Receipt &gt; Generate Receipts</span>
   </div> 
        </Stack> */}

        <Stack direction="row" alignItems="left" gap={1} mb={3}>
          <ReceiptIcon color="primary" />
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
            Generate Receipt
          </Typography>
        </Stack>

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ mb: 3 }}
        >
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {/* ══ STEP 0 — Select Customer ════════════════════════════════════════ */}
        {activeStep === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="primary" fontSize="small" />
                <Typography fontWeight={600}>Select Customer</Typography>
              </Stack>

              {customersLoading
                ? <Skeleton variant="rounded" height={56} />
                : (
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(c) => `${c.customerName ?? c.companyName ?? ""}${c.phone ? " | " + c.phone : ""}`}
                    value={selectedCustomer}
                    onChange={(_, v) => handleCustomerSelect(v)}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    renderInput={(params) => (
                      <TextField {...params} label="Search customer by name or phone" fullWidth />
                    )} />
                )}

              {selectedCustomer && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                  <Typography fontWeight={600}>
                    {selectedCustomer.customerName ?? selectedCustomer.companyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[selectedCustomer.street, selectedCustomer.city,
                    selectedCustomer.state, selectedCustomer.zipCode]
                      .filter(Boolean).join(", ")}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                    {selectedCustomer.phone && <Chip label={`📞 ${selectedCustomer.phone}`} size="small" />}
                    {selectedCustomer.GSTIN && <Chip label={`GSTIN: ${selectedCustomer.GSTIN}`} size="small" />}
                    {selectedCustomer.state && <Chip label={selectedCustomer.state} size="small" variant="outlined" />}
                  </Stack>
                </Paper>
              )}

              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" disabled={!selectedCustomer}
                  onClick={() => setActiveStep(1)}>
                  Next: View Outstanding
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ══ STEP 1 — Review Outstanding ═════════════════════════════════════ */}
        {activeStep === 1 && selectedCustomer && (
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={2}>
                Outstanding for{" "}
                <Box component="span" color="primary.main">
                  {selectedCustomer.customerName ?? selectedCustomer.companyName}
                </Box>
              </Typography>

              {/* Summary cards */}
              <Grid container spacing={2} mb={2}>
                {invoicesLoading
                  ? [1, 2, 3].map((k) => (
                    <Grid item xs={12} sm={4} key={k}>
                      <Skeleton variant="rounded" height={72} />
                    </Grid>
                  ))
                  : [
                    { label: "Total Billed", value: `₹${totalBilled.toLocaleString("en-IN")}` },
                    { label: "Amount Received", value: `₹${totalPaidSoFar.toLocaleString("en-IN")}`, color: "success.main" },
                    {
                      label: "Total Outstanding",
                      value: `₹${totalOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                      color: totalOutstanding > 0 ? "error.main" : "success.main"
                    },
                  ].map((s) => (
                    <Grid item xs={12} sm={4} key={s.label}>
                      <StatCard label={s.label} value={s.value} color={s.color} />
                    </Grid>
                  ))}
              </Grid>

              {/* Filter bar */}
              <Stack direction={isMobile ? "column" : "row"} gap={1.5} mb={1.5} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="Search invoice no..."
                  value={invoiceSearch}
                  onChange={(e) => { setInvoiceSearch(e.target.value); setInvoicePage(1); } }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220 }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter} label="Status"
                    onChange={(e) => { setStatusFilter(e.target.value as any); setInvoicePage(1); } }
                    startAdornment={<FilterListIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: "action.active" }} />}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="due">Pending / Due</MenuItem>
                    <MenuItem value="paid">Settled</MenuItem>
                  </Select>
                </FormControl>

                {selectedInvoices.length > 0 && (
                  <Chip
                    color="primary"
                    label={`${selectedInvoices.length} selected — Due: ₹${selectedDueTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                    onDelete={() => { setSelectedInvoices([]); setPaidAmount(""); } } />
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Check one or more pending invoices — amount is auto-filled as their combined due.
              </Typography>

              {/* Invoice list */}
              {invoicesLoading ? (
                <Skeleton variant="rounded" height={180} />
              ) : filteredInvoices.length === 0 ? (
                <Alert severity="info">No invoices match the current filter.</Alert>
              ) : isMobile ? (
                /* ── Mobile cards ── */
                <Stack gap={1.5}>
                  {pagedInvoices.map((inv) => {
                    const settled = Number(inv.dueAmount) <= 0;
                    const isSel = isSelected(inv);
                    return (
                      <Paper key={inv.invoiceId} variant="outlined"
                        onClick={() => toggleInvoice(inv)}
                        sx={{
                          p: 1.5, borderRadius: 2,
                          cursor: settled ? "default" : "pointer",
                          // ✅ FIXED highlight
                          border: isSel
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.divider}`,
                          bgcolor: isSel
                            ? `${theme.palette.primary.main}14`
                            : "background.paper",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" alignItems="center" gap={1}>
                            {!settled && (
                              <Checkbox size="small" checked={isSel}
                                onChange={() => toggleInvoice(inv)}
                                onClick={(e) => e.stopPropagation()} />
                            )}
                            <Box>
                              <Typography fontWeight={600} fontSize={13}>
                                {inv.invoiceNumber || inv.invoiceId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(inv.invoiceDate).toLocaleDateString("en-GB")}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip label={settled ? "Settled" : "Pending"} size="small"
                            color={settled ? "success" : "warning"} />
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={1}>
                          {[
                            { l: "Total", v: `₹${Number(inv.grandTotal).toLocaleString("en-IN")}` },
                            { l: "Paid", v: `₹${Number(inv.totalPaidAmount).toLocaleString("en-IN")}`, c: "success.main" },
                            {
                              l: "Due", v: `₹${Number(inv.dueAmount).toLocaleString("en-IN")}`,
                              c: settled ? "success.main" : "error.main"
                            },
                          ].map(({ l, v, c }) => (
                            <Grid item xs={4} key={l}>
                              <Typography variant="caption" color="text.secondary" display="block">{l}</Typography>
                              <Typography fontSize={13} fontWeight={500} color={c}>{v}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                /* ── Desktop table ── */
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "grey.50" }}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Tooltip title="Select all pending on this page">
                            <Checkbox size="small"
                              indeterminate={someSelected}
                              checked={allPageSelected}
                              onChange={toggleSelectAllOnPage} />
                          </Tooltip>
                        </TableCell>
                        {["Invoice No.", "Date", "Total (₹)", "Paid (₹)", "Due (₹)", "Status"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedInvoices.map((inv) => {
                        const settled = Number(inv.dueAmount) <= 0;
                        const isSel = isSelected(inv);
                        return (
                          <TableRow key={inv.invoiceId}
                            hover={!settled}
                            onClick={() => toggleInvoice(inv)}
                            sx={{
                              cursor: settled ? "default" : "pointer",
                              // ✅ FIXED highlight
                              bgcolor: isSel
                                ? `${theme.palette.primary.main}14`
                                : "inherit",
                              "& td:first-of-type": {
                                borderLeft: isSel
                                  ? `4px solid ${theme.palette.primary.main}`
                                  : "4px solid transparent",
                              },
                              transition: "background-color 0.15s ease",
                            }}
                          >
                            <TableCell padding="checkbox"
                              onClick={(e) => e.stopPropagation()}>
                              {!settled && (
                                <Checkbox size="small" checked={isSel}
                                  onChange={() => toggleInvoice(inv)} />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, color: "primary.main" }}>
                              {inv.invoiceNumber || inv.invoiceId}
                            </TableCell>
                            <TableCell>
                              {new Date(inv.invoiceDate).toLocaleDateString("en-GB")}
                            </TableCell>
                            <TableCell>{Number(inv.grandTotal).toLocaleString("en-IN")}</TableCell>
                            <TableCell sx={{ color: "success.main" }}>
                              {Number(inv.totalPaidAmount).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell sx={{ color: settled ? "success.main" : "error.main", fontWeight: 600 }}>
                              {Number(inv.dueAmount).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell>
                              <Chip label={settled ? "Settled" : "Pending"} size="small"
                                color={settled ? "success" : "warning"} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination count={totalPages} page={invoicePage}
                    onChange={(_, p) => setInvoicePage(p)}
                    color="primary" size={isMobile ? "small" : "medium"} />
                </Box>
              )}

              <Stack direction={isMobile ? "column" : "row"} gap={1} mt={2}
                justifyContent="space-between">
                <Button variant="outlined" onClick={() => setActiveStep(0)}>Back</Button>
                <Button variant="contained" disabled={invoicesLoading}
                  onClick={() => setActiveStep(2)}>
                  Next: Enter Payment
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ══ STEP 2 — Payment Details ═════════════════════════════════════════ */}
        {activeStep === 2 && selectedCustomer && (
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <PaymentIcon color="primary" fontSize="small" />
                <Typography fontWeight={600}>Payment Details</Typography>
              </Stack>

              {/* Selected invoice summary */}
              {selectedInvoices.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedInvoices.length === 1 ? (
                    <>Linked to <strong>{selectedInvoices[0].invoiceNumber || selectedInvoices[0].invoiceId}</strong>
                      {" "}— Due: ₹{Number(selectedInvoices[0].dueAmount).toFixed(2)}</>
                  ) : (
                    <><strong>{selectedInvoices.length} invoices selected</strong>
                      {" "}— Combined due: ₹{selectedDueTotal.toFixed(2)}<br />
                      <Typography variant="caption">
                        {selectedInvoices.map((i) => i.invoiceNumber || i.invoiceId).join(", ")}
                      </Typography></>
                  )}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount Received (₹)" type="number"
                    value={paidAmount}
                    onChange={(e) => { setPaidAmount(e.target.value); setSuccessData(null); } }
                    fullWidth inputProps={{ min: 1, step: "0.01" }}
                    error={overpayment}
                    helperText={selectedInvoices.length > 0
                      ? `Selected invoices due: ₹${selectedDueTotal.toFixed(2)}`
                      : `Total outstanding: ₹${totalOutstanding.toFixed(2)}`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Mode</InputLabel>
                    <Select value={paymentMode} label="Payment Mode"
                      onChange={(e) => setPaymentMode(e.target.value)}>
                      {["Cash", "UPI", "NEFT", "RTGS", "Cheque", "Card", "Online Transfer"].map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Receipt Date" type="date"
                    value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)}
                    fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Reference / UTR / Cheque No. (Optional)"
                    value={utrNo} onChange={(e) => setUtrNo(e.target.value)}
                    fullWidth placeholder="e.g. UTR12345678" />
                </Grid>
              </Grid>

              {overpayment && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Amount ₹{amount.toLocaleString("en-IN")} exceeds outstanding
                  ₹{totalOutstanding.toLocaleString("en-IN")}. Will be recorded as advance.
                </Alert>
              )}
              {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}

              {/* Preview */}
              {amount > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Receipt Preview</Typography>
                  {[
                    ["Customer", selectedCustomer.customerName ?? selectedCustomer.companyName],
                    selectedInvoices.length > 0
                      ? ["Against Invoice(s)", selectedInvoices.map((i) => i.invoiceNumber || i.invoiceId).join(", ")]
                      : ["Reference", "General Payment"],
                    ["Payment Mode", paymentMode],
                    ["Date", new Date(receiptDate).toLocaleDateString("en-GB")],
                    ...(utrNo ? [["Ref / UTR", utrNo]] : []),
                  ].map(([k, v]: any) => (
                    <Stack key={k} direction="row" justifyContent="space-between" py={0.4}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" sx={{ maxWidth: "60%", textAlign: "right" }}>{v}</Typography>
                    </Stack>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={700}>Amount Received</Typography>
                    <Typography fontWeight={700}>
                      ₹{amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" mt={0.5}>
                    <Typography variant="body2" color="text.secondary">Balance after payment</Typography>
                    <Typography variant="body2" fontWeight={500}
                      color={balanceAfter > 0 ? "error.main" : "success.main"}>
                      ₹{balanceAfter.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </Typography>
                  </Stack>
                </Paper>
              )}

              <Stack direction={isMobile ? "column" : "row"} gap={1} mt={2}
                justifyContent="space-between">
                <Button variant="outlined" onClick={() => setActiveStep(1)}>Back</Button>
                <Button variant="contained"
                  startIcon={submitting
                    ? <CircularProgress size={16} color="inherit" />
                    : <ReceiptIcon />}
                  disabled={!selectedCustomer || amount <= 0 || submitting}
                  onClick={handleSubmit}>
                  {submitting ? "Generating..." : "Generate Receipt & Print PDF"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ══ STEP 3 — Success ════════════════════════════════════════════════ */}
        {activeStep === 3 && successData && (
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2, textAlign: "center" }}>
            <CardContent sx={{ py: 4 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 1 }} />
              <Typography variant="h6" fontWeight={700} mb={0.5}>
                Receipt Generated Successfully!
              </Typography>
              <Typography color="text.secondary" mb={3}>
                Receipt ID: <strong>{successData.receiptId}</strong>
              </Typography>

              <Grid container spacing={2} justifyContent="center" mb={3}>
                <Grid item xs={12} sm={4}>
                  <StatCard label="Amount Received"
                    value={`₹${successData.paidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                    color="primary.main" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard label="Balance Outstanding"
                    value={`₹${successData.balanceAfter.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                    color={successData.balanceAfter > 0 ? "error.main" : "success.main"} />
                </Grid>
              </Grid>

              <Alert severity="success" sx={{ mb: 2, textAlign: "left" }}>
                PDF downloaded automatically. Use the button below to re-print.
              </Alert>

              <Stack direction={isMobile ? "column" : "row"} gap={2} justifyContent="center">
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintAgain}>
                  Print PDF Again
                </Button>
                <Button variant="contained" onClick={handleReset}>
                  Generate Another Receipt
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Container>
        <Box sx={{ width: "100%", mt: isMobile ? 4 : 10 }}>
              <Footer />
        </Box>
      </>
  );
};
export default GenerateReceipt;